import { BufferAttribute, BufferGeometry, Color, DoubleSide, Group, Matrix4, Mesh, MeshLambertMaterial, Vector3 } from 'three';
import type { Engine } from '../engine';
import type { TriangleSoup } from '../design-region-bounds';
import { thinRegionSolids, ThinRegionSolid, ThinRegionsMessage, ThinRegionsRequest } from '../thin-regions';

// Apart from the orange design parts, the magenta design-overlap overlay and
// the red/blue sharp-edge overlay. Thin walls on a part the warning does not
// name are dimmer.
const THIN_COLOR = new Color('#06b6d4');
const THIN_UNNAMED_COLOR = new Color('#64748b');

/**
 * Admin overlay: where the design parts are thinner than the optimizer's
 * smallest feature, as the Run dialog's thin-design warning measures it.
 * Parts the warning names are drawn bright, other thin walls dim. Drawn on
 * the part's surface, under each design part's group; the thickness is
 * measured in a worker.
 */
export class ThinRegionsManager {
  private enabled = false;
  private shown = false;
  private hasDesign = false;
  private refreshQueued = false;
  private worker: Worker | null = null;
  private job = 0;
  private fraction: number | null = null;
  // The threshold and design parts the overlay was last built for.
  private builtFor: string | null = null;
  private thresholdMm: () => number = () => 0;
  private readonly holders = new Map<string, Group>();

  /** Called whenever {@link available} changes. */
  onAvailabilityChange: ((available: boolean) => void) | null = null;
  /** Called whenever {@link progress} changes. */
  onProgress: ((progress: number | null) => void) | null = null;

  constructor(private readonly engine: Engine) {}

  get visible(): boolean {
    return this.shown;
  }

  /** Whether there is a design part to check. */
  get available(): boolean {
    return this.hasDesign;
  }

  /** How far the overlay is built, 0 to 1; null when it is not being built. */
  get progress(): number | null {
    return this.fraction;
  }

  /**
   * Starts tracking availability; nothing is measured before this, so only admins pay for it.
   * @param thresholdMm - Reads the current thin threshold in mm, as the thin-design warning uses it
   */
  enable(thresholdMm: () => number): void {
    this.enabled = true;
    this.thresholdMm = thresholdMm;
    this.refresh();
  }

  show(): void {
    this.shown = true;
    this.rebuild();
  }

  hide(): void {
    this.shown = false;
    this.cancel();
    this.clear();
    this.builtFor = null;
    this.engine.requestRender();
  }

  /**
   * Re-checks availability, and the overlay against the current design parts
   * and threshold, once per tick. Call it when a part changes type or the
   * resolution or minimum feature size changes.
   */
  refresh(): void {
    if (!this.enabled || this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      this.update();
    });
  }

  private update(): void {
    const available = this.engine.objectsDesign.length > 0;
    if (available !== this.hasDesign) {
      this.hasDesign = available;
      // Nothing left to show: the overlay goes off with the switch.
      if (!available && this.shown) this.hide();
      this.onAvailabilityChange?.(available);
    }
    if (this.shown && this.inputsKey() !== this.builtFor) this.rebuild();
  }

  private inputsKey(): string {
    return [this.thresholdMm(), ...this.engine.objectsDesign.map((object) => String(object.data.id))].join('|');
  }

  private rebuild(): void {
    this.cancel();
    this.clear();
    this.builtFor = this.inputsKey();
    const thresholdMm = this.thresholdMm();
    const parts = this.engine.objectsDesign
      .map((object) => ({ id: String(object.data.id), soup: this.engine.partTriangleSoup(object) }))
      .filter((part): part is { id: string; soup: TriangleSoup } => part.soup !== null);
    if (!(thresholdMm > 0) || parts.length === 0) {
      this.engine.requestRender();
      return;
    }
    const request: ThinRegionsRequest = { parts, thresholdMm };

    if (typeof Worker === 'undefined') {
      this.draw(thinRegionSolids(request));
      return;
    }
    const job = ++this.job;
    const worker = new Worker(new URL('./thin-regions.worker', import.meta.url), { type: 'module' });
    this.worker = worker;
    this.setProgress(0);
    worker.onmessage = (event: MessageEvent<ThinRegionsMessage>) => {
      if (job !== this.job) return;
      const message = event.data;
      if (message.type === 'progress') {
        this.setProgress(message.total === 0 ? 1 : message.done / message.total);
        return;
      }
      this.cancel();
      if (message.type === 'done') this.draw(message.solids);
      else console.warn('Thin regions:', message.message);
    };
    worker.onerror = (error) => {
      if (job !== this.job) return;
      this.cancel();
      console.warn('Thin regions:', error.message);
    };
    worker.postMessage(request, parts.map((part) => part.soup.buffer));
  }

  /** Stops the build in progress, if any; its results are dropped. */
  private cancel(): void {
    this.job++;
    this.worker?.terminate();
    this.worker = null;
    this.setProgress(null);
  }

  private setProgress(progress: number | null): void {
    if (progress === this.fraction) return;
    this.fraction = progress;
    this.onProgress?.(progress);
  }

  private draw(solids: readonly ThinRegionSolid[]): void {
    for (const { id, positions, named } of solids) {
      const object = this.engine.objectsDesign.find((o) => String(o.data.id) === id);
      if (!object) continue;
      // Measured in world space, as the warning measures it; drawn in the part's group.
      object.group.updateWorldMatrix(true, false);
      toLocal(positions, new Matrix4().copy(object.group.matrixWorld).invert());
      const holder = this.buildHolder(positions, named);
      object.group.add(holder);
      this.holders.set(id, holder);
    }
    this.engine.requestRender();
  }

  private clear(): void {
    for (const holder of this.holders.values()) {
      holder.removeFromParent();
      holder.traverse((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
          (child.material as MeshLambertMaterial).dispose();
        }
      });
    }
    this.holders.clear();
  }

  // Wrapped in a group, as the sharp-edge overlay is: the hover outline takes
  // every Mesh directly under the part's group.
  private buildHolder(positions: Float32Array, named: boolean): Group {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.computeVertexNormals();
    const material = new MeshLambertMaterial({
      color: named ? THIN_COLOR : THIN_UNNAMED_COLOR,
      // The triangles lie on the part's own faces: pulled toward the camera
      // so they win the depth test against them. Both sides, as the pieces
      // keep the part's winding, whichever way it runs.
      side: DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
      flatShading: true
    });
    const mesh = new Mesh(geometry, material);
    mesh.name = 'thin-regions';
    mesh.renderOrder = 10;
    // Not pickable: clicks go to the part underneath.
    mesh.raycast = () => {};

    const holder = new Group();
    holder.name = 'thin-regions';
    holder.add(mesh);
    return holder;
  }
}

/** Moves non-indexed positions by `matrix`, in place. */
function toLocal(positions: Float32Array, matrix: Matrix4): void {
  const vertex = new Vector3();
  for (let i = 0; i < positions.length; i += 3) {
    vertex.fromArray(positions, i).applyMatrix4(matrix);
    vertex.toArray(positions, i);
  }
}
